import { get } from 'lodash-es'
import type { Collections, Record as VuexOrmRecord } from '@vuex-orm/core'
import type * as Contracts from '@vuex-orm/core/dist/src/query/contracts'
import { inject, injectable, injectFromBase, unmanaged } from 'inversify'
import type BaseModel from '@/data/models/BaseModel'
import type LoggerService from '@/services/loggerService'
import ORMBaseRepository from '@/data/repositories/ormBaseRepository'
import type { LowLevelInsertBody, LowLevelUpdateBody } from '@/services/vuex/types'
import type {
  CreatedEntityData,
  Transportable,
  TransportResponse,
  UpdatedEntityResponse,
} from '@/services/transport/types'
import { TmRepositoryError } from '@/core/error/tmRepositoryError'
import { TmIncorrectContractError } from '@/core/error/tmIncorrectContractError'
import { SERVICE_TYPES } from '@/core/container/types'
import ModelMapper from '@/data/utils/modelMapper'
import type BaseStorageTransport from '@/services/transport/storage/baseStorageTransport'
import type { ModelRaw } from '@/types'

@injectable()
@injectFromBase()
export default abstract class OrmStorageRepository<T extends BaseModel>
  extends ORMBaseRepository<T, string>
  implements Transportable<T, undefined>
{
  protected transport: BaseStorageTransport
  protected constructor(
    @unmanaged() storageService: BaseStorageTransport,
    @inject(SERVICE_TYPES.LoggerService) loggerService: LoggerService,
  ) {
    super(loggerService)
    this.transport = storageService
  }

  public getApiSource() {
    return this.transport
  }

  public async insertOrUpdate(payload: VuexOrmRecord[]) {
    const apiModel = await this.getApiSource().get(this.model().name)

    for (const record of payload) {
      if (get(apiModel.data, record.id)) {
        this.update([record] as LowLevelUpdateBody<T>[])
      } else {
        this.insert([record] as LowLevelInsertBody<T>[])
      }
    }
    return payload as unknown as Promise<Collections>
  }

  public async fillAll(isReset = false): Promise<T[]> {
    this.logGroupStart('fillAll')
    const result = await this.getApiSource().get<T[]>(this.settings().single as string)
    if (!result.data) {
      throw new TmIncorrectContractError('Response data expected')
    }

    await this.insertOrUpdate(Object.values(result.data))
    this.logGroupEnd()
    return result.data
  }

  protected getPath(key: string, id: string) {
    return `${key}${this.getRepoSeparator()}${id}`
  }

  protected getUpdatePath(key: string, data: ModelRaw<Partial<T>>): string {
    return this.getPath(key, data.id)
  }

  protected getRepoSeparator() {
    return this.transport.getPathSeparator ? this.transport.getPathSeparator() : '/'
  }

  protected serializeData(data: ModelRaw<Partial<T>>) {
    return data
  }

  protected log(message: string) {
    this.loggerService.log('orm', message, 'storageRepository')
  }

  protected logError(message: string) {
    this.loggerService.error('orm', message, 'storageRepository')
  }

  protected async lowLevelDeleteByCondition(condition: Contracts.Predicate<T>, stiModel?: typeof BaseModel) {
    super.lowLevelDeleteByCondition(condition, stiModel)

    const singleKey = this.settings().single
    if (!singleKey) {
      throw new TmRepositoryError(`${this.model().entity} storage repository without 'single' in settings`)
    }
    const records = (await this.transport.get<T[]>(singleKey)).data
    if (!records) {
      throw new TmIncorrectContractError('Response data expected')
    }

    Object.values<T>(records).forEach((model) => {
      if (condition(model)) {
        this.transport.delete(`${singleKey}${this.getPathSeparator()}${model.id}`)
      }
    })
  }

  protected getPathSeparator() {
    if (!this.transport.getPathSeparator) {
      throw new TmRepositoryError(`${this.model().entity} repository wrong transport`)
    }

    return this.transport.getPathSeparator()
  }

  protected async doGet<M>(key: string, id: string) {
    return this.getApiSource().get<M>(this.getPath(key, id))
  }

  protected async doDelete(key: string, id: string) {
    return this.getApiSource().delete(this.getPath(key, id))
  }

  protected doPost<ResponseType = CreatedEntityData>(key: string, toCreate: ModelRaw<Partial<T>>) {
    const apiSource = this.getApiSource()
    const path = this.getUpdatePath(key, toCreate)
    return apiSource.post<ResponseType>(path, toCreate)
  }

  protected async doPut(key: string, updated: ModelRaw<Partial<T>>) {
    return this.getApiSource().put<UpdatedEntityResponse>(this.getUpdatePath(key, updated), this.serializeData(updated))
  }

  /**
   * Transport
   */
  public override async getRequest(id: string): Promise<ModelRaw<T>> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }
    const response = await this.doGet<ModelRaw<T>>(this.settings().single!, id)
    response.data = ModelMapper.normalizePayload(response.data, this.model())
    return response.data
  }

  public putRequest(toUpdate: ModelRaw<Partial<T>>): Promise<TransportResponse<UpdatedEntityResponse>> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }
    return this.doPut(this.settings().single!, toUpdate)
  }

  public postRequest<ResponseType = CreatedEntityData>(
    toCreate: ModelRaw<Partial<T>>,
  ): Promise<TransportResponse<ResponseType>> {
    if (!this.settings().create) {
      throw new TmRepositoryError(`You must define \`create\` property in settings() for entity ${this.model().entity}`)
    }
    return this.doPost<ResponseType>(this.settings().create!, toCreate)
  }
  public deleteRequest(id: string): Promise<unknown> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }

    return this.doDelete(this.settings().single!, id)
  }
}
